import { PlayerSwap } from "@/api/privateRequests/sbcs/player-swap";
import { addAlert, setMessage } from "@/redux/message/messageSlice";
import {
  removeIncludedPlayer,
  sbcFiltersSelector,
} from "@/redux/sbcs/sbcFiltersSlice";
import {
  fetchSbc,
  fetchSbcSet,
  getSbcSelector,
  getSbcSetSelector,
  getSbcsSelector,
  markSbcSolved,
  solveSbc,
  updateSolution,
} from "@/redux/sbcs/sbcsSlice";
import { AppDispatch } from "@/redux/store";
import { fetchUser, getUserSelector } from "@/redux/user/userSlice";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";

import { deleteSpecificPlayers } from "../../api/privateApi";
import SolutionView from "../../components/organisms/SolutionView/SolutionView";
import "../../redux/user/userSlice";

export const SbcSolutionPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { sbcId, setId } = useParams();
  const sbc = useSelector(getSbcSelector(+sbcId, +setId));
  const sbcSet = useSelector(getSbcSetSelector(+setId));
  const { solution } = useSelector(getSbcsSelector);
  const { user } = useSelector(getUserSelector);
  const filters = useSelector(sbcFiltersSelector);
  const [showSwapView, setShowSwapView] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!solution && sbcId) {
      dispatch(solveSbc(+sbcId));
    }
  }, [sbcId]);

  useEffect(() => {
    if (!sbc) {
      dispatch(fetchSbc({ sbcId: +sbcId, setId: +setId }));
      dispatch(fetchSbcSet({ setId: +setId }));
    }
  }, [sbcId, sbc, setId, dispatch]);

  const onSwap = (player: PlayerSwap, index: number) => {
    const { players } = solution;
    const newPlayers = [...players];
    const formation = sbc.formation;

    for (let i = 0; i < newPlayers.length; i++) {
      const p = { ...newPlayers[i] };
      p.chemistry = player.new_solution_info.player_chems[i];
    }
    const untradeablesCount = player.new_solution_info.n_untradeables;
    const ownedPlayersCount = player.new_solution_info.n_owned_players;
    const rating = player.new_solution_info.rating;
    const chem = player.new_solution_info.total_chem;
    const transferMarketCost = player.new_solution_info.transfer_market_cost;
    const duplicatesCount = player.new_solution_info.n_duplicates;
    const cost = player.new_solution_info.price;
    newPlayers[index] = player;

    dispatch(
      updateSolution({
        ...solution,
        cost,
        chem,
        rating,
        players: newPlayers,
        formation,
        untradeablesCount,
        ownedPlayersCount,
        transferMarketCost,
        duplicatesCount,
      }),
    );
  };

  const handleUncompleteSBC = () => {
    dispatch(
      markSbcSolved({
        sbcId: sbc.id,
        setId: sbc.setId,
        solved: false,
      }),
    );
  };

  const handleCompleteAndRemovePlayers = async (solved: boolean) => {
    const ownedPlayers = solution?.players?.filter(
      (player) => player.user_owned,
    );
    if (!user.uuid) {
      dispatch(
        setMessage({
          title: "Please login to mark sbc as solved",
        }),
      );
      return;
    }
    if (solved) {
      if (filters.playersFrom !== "market" && ownedPlayers?.length > 0) {
        await deleteSpecificPlayers(ownedPlayers?.map((player) => player.id));
        dispatch(fetchUser({ didLogin: false }));
      }
      solution.players.forEach((p) =>
        dispatch(removeIncludedPlayer(p.resource_id)),
      );

      const count = ownedPlayers?.length ?? 0;
      const desc = count === 0 ? "" : `${count} Players removed from import`;

      dispatch(
        addAlert({
          title: `Completed ${sbc?.name}`,
          description: desc,
          type: "success",
        }),
      );
    }
    dispatch(
      markSbcSolved({
        sbcId: sbc.id,
        setId: sbc.setId,
        solved: solved,
      }),
    );
  };

  return (
    <div>
      <Helmet>
        <title>{`SBC Solution - ${sbc?.name}`}</title>
        <link rel="canonical" href={window.location.href} />
        <meta name="description" content={`SBC Solution - ${sbc?.name}`} />
      </Helmet>
      <SolutionView
        solution={solution}
        sbc={sbc}
        sbcSetUrl={sbcSet?.sbcsCount < 2 ? sbcSet?.imageURL : sbc?.imageURL}
        hybridSliderValue={
          filters?.playersFrom === "both"
            ? 1
            : filters?.playersFrom === "club"
              ? 2
              : 0
        }
        useUntradeable={filters?.useUntradeablesOnly}
        excludeGoldPlayers={filters?.excludeGoldPlayers}
        excludeSilverPlayers={filters?.excludeSilverPlayers}
        excludeBronzePlayers={filters?.excludeBronzePlayers}
        excludeActiveSquad={filters?.excludeActiveSquad}
        excludeObjectivePlayers={filters?.excludeObjectivePlayers}
        excludeSpecialPlayers={filters?.excludeSpecialPlayers}
        excludeSbcPlayers={filters?.excludeSbcPlayers}
        includeTransferlist={filters?.includeTransferlist}
        prioritizeDuplicates={filters?.prioritizeDuplicates}
        showSwapView={showSwapView}
        toggleSwapView={(showSwap) => setShowSwapView(showSwap)}
        onSwap={onSwap}
        onHandleSolveAgain={() => dispatch(solveSbc(+sbcId))}
        onHandleCompleteAndRemovePlayers={() =>
          handleCompleteAndRemovePlayers(true)
        }
        handleUncompleteSBC={handleUncompleteSBC}
      />
    </div>
  );
};

export default SbcSolutionPage;
